/**
 * trace-content — shared shape-handling for trace `input` / `output` payloads.
 *
 * Trace input/output is JSONB on the wire. The dashboard list endpoints
 * already return string previews via the server-side `toPreview` (Phase 2),
 * but the trace-detail endpoint may surface raw structured content. This
 * helper normalizes the messy input cases into a single
 * `{ text, isJson }` pair so the panel can render either as markdown
 * (`isJson=false`) or as a fenced code block (`isJson=true`).
 *
 * Mirrors the server-side `toPreview` rules:
 *   - string                   → render directly
 *   - { messages: [...] }      → render the LAST user message (or last entry)
 *   - { text }                 → render `text`
 *   - { content }              → render `content` (string or stringified)
 *   - anything else            → JSON.stringify(..., null, 2), isJson=true
 */

/** Result of `extractDisplayText`. */
export interface TraceDisplay {
  /** Renderable string. Empty string when the payload is null / undefined / empty. */
  text: string;
  /**
   * When true, the renderer should wrap the text in a triple-backtick block
   * (or `<pre>`) instead of running it through markdown. This catches the
   * "fell back to JSON.stringify" case where the payload is a struct we
   * couldn't reduce to a meaningful string.
   */
  isJson: boolean;
}

interface MessageLike {
  role?: string;
  content?: unknown;
  text?: unknown;
}

interface MessagesEnvelope {
  messages: MessageLike[];
}

function isMessagesEnvelope(v: unknown): v is MessagesEnvelope {
  return (
    typeof v === 'object' &&
    v !== null &&
    'messages' in v &&
    Array.isArray((v as { messages: unknown }).messages)
  );
}

function stringifyMessageContent(content: unknown): string | null {
  if (content == null) return null;
  if (typeof content === 'string') return content;
  // Some providers return content as `[{type: 'text', text: '...'}, ...]`.
  if (Array.isArray(content)) {
    const parts: string[] = [];
    for (const part of content) {
      if (typeof part === 'string') {
        parts.push(part);
      } else if (
        part &&
        typeof part === 'object' &&
        'text' in part &&
        typeof (part as { text: unknown }).text === 'string'
      ) {
        parts.push((part as { text: string }).text);
      }
    }
    if (parts.length > 0) return parts.join('\n');
  }
  try {
    return JSON.stringify(content, null, 2);
  } catch {
    return String(content);
  }
}

/**
 * Reduce a raw trace `input` or `output` JSONB value to a display-ready
 * string. `mode` decides which message role to prefer when the payload is a
 * `{messages: [...]}` envelope: 'input' picks the last user message, 'output'
 * picks the last assistant message.
 */
export function extractDisplayText(
  raw: unknown,
  mode: 'input' | 'output',
): TraceDisplay {
  if (raw == null) return { text: '', isJson: false };

  // Plain string — happens when the server already pre-flattened the payload
  // (Phase 2 trace-detail returns this shape today).
  if (typeof raw === 'string') {
    return { text: raw, isJson: false };
  }

  if (typeof raw === 'object') {
    // {messages: [...]}
    if (isMessagesEnvelope(raw)) {
      const wantedRole = mode === 'input' ? 'user' : 'assistant';
      const messages = raw.messages;
      // Search backwards for the last message matching the wanted role; if
      // none, fall back to the last message in the array.
      let chosen: MessageLike | undefined;
      for (let i = messages.length - 1; i >= 0; i--) {
        const m = messages[i];
        if (m && typeof m === 'object' && m.role === wantedRole) {
          chosen = m;
          break;
        }
      }
      if (!chosen && messages.length > 0) chosen = messages[messages.length - 1];
      if (chosen) {
        const text =
          stringifyMessageContent(chosen.content) ??
          stringifyMessageContent(chosen.text);
        if (text != null) return { text, isJson: false };
      }
    }

    // {text: '...'}
    if (
      'text' in raw &&
      typeof (raw as { text: unknown }).text === 'string'
    ) {
      return { text: (raw as { text: string }).text, isJson: false };
    }

    // {content: ...}
    if ('content' in raw) {
      const content = (raw as { content: unknown }).content;
      const text = stringifyMessageContent(content);
      if (text != null) return { text, isJson: false };
    }
  }

  // Fall-through — stringify as JSON for the renderer to wrap in a code block.
  try {
    return { text: JSON.stringify(raw, null, 2), isJson: true };
  } catch {
    return { text: String(raw), isJson: true };
  }
}
